import { inject, Injectable } from "@angular/core";
import { createStore, select, setProp, withProps } from "@ngneat/elf";
import { localStorageStrategy, persistState } from "@ngneat/elf-persist-state";

import { CmsDataService } from "../services/cms.service";
import { selectLoading, withLoading, withUpdatedAt } from "../store/stateprops";
import { Observable, of, tap } from "rxjs";
import { Translation, TranslocoService } from "@jsverse/transloco";
import { CmsDataState } from "../models/cms-data.model";
import { environment } from "../../../environments/environment";

const initialState: CmsDataState = {
    data: null,
    updatedAt: null,
};

@Injectable({ providedIn: "root" })
export class CmsStoreRepository {
    cmsService = inject(CmsDataService);
    translocoService = inject(TranslocoService);

    private cmsDataStore = this.createStore();

    isLoading$ = this.cmsDataStore.pipe(selectLoading());
    data$ = this.cmsDataStore.pipe(select((state) => state.data));
    lang = this.translocoService.getActiveLang();

    private createStore(): typeof store {
        const store = createStore(
            { name: 'data' },
            withProps<CmsDataState>(initialState),
            withUpdatedAt(),
            withLoading()
        );
        persistState(store, {
            storage: localStorageStrategy,
        });
        return store;
    }

    getCmsStore() {
        return this.cmsDataStore.getValue();
    }

    isCacheExpired(maxAgeMs: number): boolean {
        const lastUpdated = this.cmsDataStore.getValue().updatedAt;
        if (!lastUpdated) {
            return false;
        }
        return !lastUpdated || Date.now() - new Date(lastUpdated).getTime() > maxAgeMs;
    }

    getData$(): Observable<Translation> {
        const cachedData = this.cmsDataStore.getValue().data;

        if (cachedData) {
            if (this.isCacheExpired(environment.cmsCacheExpiration)) {
                this.cmsService.getCmsDataAsJson$(this.lang).pipe(
                    tap((data: Translation) => {
                        this.setTranslationData(data);
                        this.updateCmsDataStore(data);
                        })
                ).subscribe({
                    next: () => console.log("Cache refreshed"),
                    error: (error) => console.error("Error refreshing cache:", error),
                });
            }
            return of(cachedData);
        }

        return this.cmsService.getCmsDataAsJson$(this.translocoService.getActiveLang()).pipe(
            tap((data) => {
                this.setTranslationData(data);
                this.updateCmsDataStore(data);
            })
        );
    }

    loadCmsData$(): Observable<Translation> {
        return this.getData$().pipe(
            tap((data) => {
                this.setTranslationData(data);
            })
        );
    }

    setTranslationData(data: Translation): void {
        const lang = this.translocoService.getActiveLang();

        this.translocoService.load(lang).subscribe(base => {
            this.translocoService.setTranslation(this.FullStopReplace(data), lang, { merge: true });
        });
    }

    private FullStopReplace(data: Translation){
    return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
            key.replace(/_/g, '.'),
            value
        ])
        );
    }

    updateCmsDataStore(data: Translation): void {
        this.cmsDataStore.update(setProp("data", data), setProp("updatedAt", new Date()));
    }

    clearData() {
        this.cmsDataStore.reset();
    }

}